import { useMemo } from "react";
import { useDataSourceColumns } from "@/hooks/data/useDataSourceColumns";
import { useProcessedData } from "@/stores/data-processing";
import { formatNumber } from "@/utils/format";

/**
 * Hook to compute column options with unique value counts
 */
export function useColumnOptions(activeDataflowId: string | null) {
  const availableColumns = useDataSourceColumns();
  const processedData = useProcessedData(activeDataflowId || "");

  return useMemo(() => {
    if (!processedData?.data || !availableColumns.length) {
      return availableColumns.map((col) => ({
        value: col,
        label: col,
        uniqueCount: 0,
      }));
    }

    const options = availableColumns.map((column) => {
      const uniqueValues = new Set<string>();
      processedData.data.forEach((row) => {
        const value = row[column];
        if (value != null && value !== "") {
          uniqueValues.add(String(value));
        }
      });

      const value = column;
      const uniqueCount = uniqueValues.size;
      const label = `${value} (${formatNumber(uniqueCount)})`;
      const disabled = uniqueCount > processedData.data.length / 2;
      return {
        value,
        label,
        uniqueCount,
        disabled,
      };
    });

    return options.sort((a, b) => a.uniqueCount - b.uniqueCount);
  }, [availableColumns, processedData]);
}

/**
 * Hook to get unique values for a group column
 */
export function useUniqueValues(
  activeDataflowId: string | null,
  groupColumnName: string | null
) {
  const processedData = useProcessedData(activeDataflowId || "");

  return useMemo(() => {
    if (!processedData?.data?.length || !groupColumnName) return [];

    const groupMap = new Map<string, number>();
    processedData.data.forEach((row) => {
      const value = row[groupColumnName];
      if (value != null && value !== "") {
        const group = String(value).trim();
        groupMap.set(group, (groupMap.get(group) || 0) + 1);
      }
    });

    return Array.from(groupMap.keys()).sort();
  }, [processedData, groupColumnName]);
}

